import { isWeb } from "@memorang/helpers/platform";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSummaryCard } from "../Cards/ProgressSummaryCard";
import { StudySessionMasteryTagProgressCard } from "../Cards/StudySessionMasteryTagProgressCard";
import MasonryGrid from "../MasonryGrid";
import type { MasteryListItemProps } from "../MasteryListItem";
import { LegacyTestsCard } from "./LegacyTestsCard";
import { PracticeTestsCard } from "./PracticeTestsCard";
import type {
	ExamCardData,
	LegacyTestsCardData,
	MasteryTagProgressData,
	SummaryCardData,
} from "./types";

interface Props {
	masteryTagsProgressData: MasteryTagProgressData[];
	needsImprovementProgressData: MasteryTagProgressData | null;
	summaryCardData?: SummaryCardData;
	practiceTestsCardData?: ExamCardData;
	legacyTestsCardData?: LegacyTestsCardData;
	showStudyPackViews?: boolean;
	showPracticeTests?: boolean;
	handleClickCard?: (title: string) => void;
	handleClickStudy?: () => void;
	appFont: string;
	handleNavigation: (numericId: number) => void;
	handleLegacyReportDownload?: (numericId: number) => void;
	downloadingLegacyReportId?: number;
	progressNeedsImprovementCtaLabel?: string;
	handleClickInfo?: (text: string, title: string) => void;
	needsImprovement?: MasteryListItemProps[];
	needsImprovementTagType?: string;
	onViewMorePracticeTests: () => void;
	onViewMoreLegacyTests?: () => void;
}

type TranslateFn = ReturnType<typeof useTranslation>["t"];

const buildProgressCards = (props: Props, t: TranslateFn): ReactElement[] => {
	const {
		masteryTagsProgressData,
		summaryCardData,
		showStudyPackViews,
		practiceTestsCardData,
		legacyTestsCardData,
		showPracticeTests,
		handleClickCard,
		handleClickStudy,
		appFont,
		handleNavigation,
		handleLegacyReportDownload,
		downloadingLegacyReportId,
		progressNeedsImprovementCtaLabel,
		handleClickInfo,
		needsImprovement,
		needsImprovementTagType,
		onViewMorePracticeTests,
		onViewMoreLegacyTests,
	} = props;

	const studyHandler = showStudyPackViews ? handleClickStudy : undefined;
	const needsImprovementCta = showStudyPackViews
		? progressNeedsImprovementCtaLabel
		: undefined;

	return [
		...(practiceTestsCardData && showPracticeTests
			? [
					<PracticeTestsCard
						{...practiceTestsCardData}
						key="practice-tests-card"
						isWeb={isWeb}
						appFont={appFont}
						handleNavigation={handleNavigation}
						onViewMore={onViewMorePracticeTests}
					/>,
				]
			: []),
		...(legacyTestsCardData
			? [
					<LegacyTestsCard
						{...legacyTestsCardData}
						key="legacy-tests-card"
						appFont={appFont}
						handleDownload={handleLegacyReportDownload ?? handleNavigation}
						downloadingId={downloadingLegacyReportId}
						onViewMore={onViewMoreLegacyTests}
					/>,
				]
			: []),
		...(summaryCardData
			? [
					<ProgressSummaryCard
						{...summaryCardData}
						key="summary-card"
						handleClickInfo={() =>
							handleClickInfo?.(summaryCardData.tooltip!, summaryCardData.title)
						}
					/>,
				]
			: []),
		...(needsImprovement?.length
			? [
					<StudySessionMasteryTagProgressCard
						key="needs-improvement"
						title={t("dashboard.skills.progressCards.needsImprovementTitle")}
						subtitle={t(
							"dashboard.skills.progressCards.needsImprovementSubtitle",
						)}
						icon="flag-outline"
						items={needsImprovement}
						tagType={needsImprovementTagType}
						showViewAll={showStudyPackViews}
						handleClickCard={studyHandler}
						cta={needsImprovementCta}
						actionsBelow
					/>,
				]
			: []),
		...masteryTagsProgressData.map((item) => (
			<StudySessionMasteryTagProgressCard
				handleClickCard={
					handleClickCard ? () => handleClickCard(item.tagType!) : undefined
				}
				key={item.title}
				{...item}
				handleClickInfo={() =>
					handleClickInfo?.(item.tooltip!, `${item.title} progress`)
				}
				actionsBelow
			/>
		)),
	];
};

const ProgressView = (props: Props) => {
	const { t } = useTranslation();

	return <MasonryGrid>{buildProgressCards(props, t)}</MasonryGrid>;
};

export default ProgressView;
